import { Reducer } from 'redux';

import { Actions } from 'store/actions/recordRequest.action';
import {
  RECORD_REQUEST_SET_PROVIDER,
  RECORD_REQUEST_SET_PATIENT,
  RECORD_REQUEST_SET_DATE,
  RECORD_REQUEST_SET_AUTHORIZATION,
  RECORD_REQUEST_SET_PAYMENT,
  RECORD_REQUEST_RESET,
  RECORD_REQUEST_LOADING,
  RECORD_REQUEST_SET_CARD_TYPE,
  RECORD_REQUEST_SET_CARD_NUMBER,
  RECORD_REQUEST_SET_PROVIDER_NAME,
  RECORD_REQUEST_SET_REQUEST_ID,
  RECORD_REQUEST_FAILURE,
  RECORD_REQUEST_PATIENT_INFO,
  RECORD_REQUEST_SET_AUTH_TYPE,
  RECORD_REQUEST_SET_ESTIMATE_AUTH,
  RECORD_REQUEST_SET_PAYMENT_METHOD,
  RECORD_REQUEST_SET_REQUEST_DETAILS,
  RECORD_REQUEST_SET_SHOW_PROMPT,
  RECORD_REQUEST_SET_RID,
  RECORD_REQUEST_SET_GOVT_ID,
  RECORD_REQUEST_SET_SUPPORTING_DOCS,
  RECORD_REQUEST_DELETE_SUPPORTING_DOC
} from 'store/types/recordRequest.type';
import { AuthFormValues, RecordDate, RequestDetail, GovtId, SupportingDocs } from 'types/requestItem';
import { RootReducerType } from './index';

export type RecordRequestStepsType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export const RecordRequestSteps: {
  Provider: RecordRequestStepsType;
  Patient: RecordRequestStepsType;
  Date: RecordRequestStepsType;
  AuthSelection: RecordRequestStepsType;
  Authorization: RecordRequestStepsType;
  GovtId: RecordRequestStepsType;
  SupportingDocs: RecordRequestStepsType;
  RequestDetails: RecordRequestStepsType;
  Payment: RecordRequestStepsType;
  Confirm: RecordRequestStepsType;
  RequestId: RecordRequestStepsType;
} = Object.freeze({
  Provider: 1,
  Patient: 2,
  Date: 3,
  AuthSelection: 4,
  Authorization: 5,
  GovtId: 6,
  SupportingDocs: 7,
  RequestDetails: 8,
  Payment: 9,
  Confirm: 10,
  RequestId: 11,
});

export type State = {
  retrievalService: any;
  provider?: any;
  requestId?: string;
  patientId?: string | null;
  cardType?: string;
  cardNumber?: string;
  date?: RecordDate;
  auth?: AuthFormValues;
  govtId?: GovtId
  supportingDocs: SupportingDocs[]
  authType?: string | undefined | null;
  payment?: string;
  currentStep: RecordRequestStepsType;
  patientInfo?: any;
  total: number;
  isLoading: boolean;
  error: string;
  estimateInitials?: string;
  paymentMethod?: string
  requestDetails?: RequestDetail
  showPrompt?: boolean
  rid?: number
};

const initialState: State = {
  currentStep: RecordRequestSteps.Provider,
  total: Object.getOwnPropertyNames(RecordRequestSteps).length,
  isLoading: false,
  supportingDocs: [],
  error: '',
  retrievalService: undefined
};

const setState = (current: RecordRequestStepsType, active: RecordRequestStepsType) => {
  return current > active ? current : active;
};

export const recordRequestReducer: Reducer<State, Actions> = (state = initialState, action) => {
  switch (action.type) {
    case RECORD_REQUEST_SET_REQUEST_ID:
      return {
        ...state,
        requestId: action.preload,
        currentStep: setState(state.currentStep, RecordRequestSteps.Confirm),
      };
    case RECORD_REQUEST_FAILURE:
      return {
        ...state,
        error: action.error,
        currentStep: setState(state.currentStep, RecordRequestSteps.Confirm),
      };
    case RECORD_REQUEST_SET_PROVIDER:
      return {
        ...state,
        provider: action.provider,
        currentStep: setState(state.currentStep, RecordRequestSteps.Patient),
        showPrompt: true
      };
    case RECORD_REQUEST_SET_PATIENT:
      return {
        ...state,
        patientId: action.id,
        patientInfo: null,
        auth: initialState.auth,
        currentStep: setState(state.currentStep, RecordRequestSteps.Date),
      };
    case RECORD_REQUEST_SET_DATE:
      return {
        ...state,
        date: action.date,
        currentStep: setState(state.currentStep, RecordRequestSteps.AuthSelection),
      };

    case RECORD_REQUEST_SET_AUTH_TYPE:
      return { ...state, authType: action.preload, currentStep: setState(state.currentStep, RecordRequestSteps.Authorization) };

    case RECORD_REQUEST_SET_AUTHORIZATION:
      return {
        ...state,
        auth: {...state.auth,...action.token},
        currentStep: setState(state.currentStep, RecordRequestSteps.GovtId),
      };

    case RECORD_REQUEST_SET_GOVT_ID:
      let govtId = { ...state.govtId, ...action.data }
      return {
        ...state,
        govtId,
        currentStep: !action.required || ((govtId.backFile || govtId.backFileURL) && (govtId.frontFile || govtId.frontFileURL)) ? setState(state.currentStep, RecordRequestSteps.SupportingDocs) : state.currentStep,
      };

    case RECORD_REQUEST_SET_SUPPORTING_DOCS:
      return {
        ...state,
        supportingDocs: action.docs,
        currentStep: setState(state.currentStep, RecordRequestSteps.RequestDetails),
      };
    case RECORD_REQUEST_DELETE_SUPPORTING_DOC: {
      let index = state.supportingDocs.findIndex(doc => doc.fileId === action.fileId)
      if (index > -1) return { ...state, supportingDocs: [...state.supportingDocs.slice(0, index), { ...state.supportingDocs[index], deleted: true }, ...state.supportingDocs.slice(index + 1)] }
      else return state
    }
    case RECORD_REQUEST_SET_PROVIDER_NAME:
      return {
        ...state,
        providerName: action.preload,
        currentStep: setState(state.currentStep, RecordRequestSteps.Provider),
      };
    case RECORD_REQUEST_SET_REQUEST_DETAILS:
      return { ...state, requestDetails: action.payload, currentStep: action.stepChange ? setState(state.currentStep, RecordRequestSteps.Payment) : state.currentStep }
    case RECORD_REQUEST_SET_PAYMENT_METHOD:
      return {
        ...state,
        paymentMethod: action.value,
        currentStep: setState(state.currentStep, RecordRequestSteps.Confirm),
      };
    case RECORD_REQUEST_SET_PAYMENT:
      return {
        ...state,
        payment: action.id,
        currentStep: setState(state.currentStep, RecordRequestSteps.Confirm),
      };
    case RECORD_REQUEST_SET_CARD_TYPE:
      return {
        ...state,
        cardType: action.preload,
        currentStep: setState(state.currentStep, RecordRequestSteps.Confirm),
      };
    case RECORD_REQUEST_SET_CARD_NUMBER:
      return {
        ...state,
        cardNumber: action.preload,
        currentStep: setState(state.currentStep, RecordRequestSteps.Confirm),
      };

    case RECORD_REQUEST_PATIENT_INFO:
      return {
        ...state,
        patientInfo: action.preload,
        patientId: null,
        auth: initialState.auth,
        currentStep: setState(state.currentStep, RecordRequestSteps.Date),
      };

    case RECORD_REQUEST_SET_ESTIMATE_AUTH:
      return { ...state, estimateInitials: action.estimateInitials }

    case RECORD_REQUEST_RESET:
      return initialState;
    case RECORD_REQUEST_LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
        currentStep: setState(state.currentStep, RecordRequestSteps.RequestId),
      };
    case RECORD_REQUEST_SET_RID:
      return { ...state, rid: action.rid }
    case RECORD_REQUEST_SET_SHOW_PROMPT:
      return { ...state, showPrompt: action.flag, requestDetails: action.data ? action.data : state.requestDetails }
    default:
      return state;
  }
};

export const recordRequestSelector = (state: RootReducerType) => state.recordRequest;
export default recordRequestReducer;